import React, { useRef, useMemo } from 'react';
import { Canvas, useFrame, useThree, extend } from '@react-three/fiber';
import * as THREE from 'three';

extend({ ShaderMaterial: THREE.ShaderMaterial });

const ShaderMaterialComponent = ({ source, uniforms, maxFps = 60 }) => {
  const ref = useRef();

  useFrame(({ clock }) => {
    if (ref.current) {
      const timestamp = clock.getElapsedTime();
      ref.current.uniforms.u_time.value = timestamp;
    }
  });

  return (
    <mesh>
      <planeGeometry args={[2, 2]} />
      <shaderMaterial
        ref={ref}
        vertexShader={`
          precision mediump float;
          void main() {
            gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
          }
        `}
        fragmentShader={source}
        uniforms={uniforms}
        glslVersion={THREE.GLSL3}
        blending={THREE.CustomBlending}
        blendSrc={THREE.SrcAlphaFactor}
        blendDst={THREE.OneFactor}
        attach="material"
      />
    </mesh>
  );
};



const DotMatrix = ({
  colors,
  opacities,
  totalSize,
  dotSize,
  shader,
  center,
}) => {
  const uniforms = useMemo(() => ({
    u_colors: { value: colors },
    u_opacities: { value: opacities },
    u_total_size: { value: totalSize },
    u_dot_size: { value: dotSize },
    u_time: { value: 0 },
    u_resolution: {
      value: new THREE.Vector2(window.innerWidth, window.innerHeight),
    },
  }), [colors, opacities, totalSize, dotSize]);

  const shaderSource = `
    precision mediump float;
    uniform vec3 u_colors[6];
    uniform float u_opacities[10];
    uniform float u_total_size;
    uniform float u_dot_size;
    uniform float u_time;
    uniform vec2 u_resolution;

    void main() {
      vec2 st = gl_FragCoord.xy / u_resolution.xy;
      st.x -= ${center.includes('x') ? '0.5' : '0.0'};
      st.y -= ${center.includes('y') ? '0.5' : '0.0'};

      float opacity = 1.0;
      vec3 color = u_colors[int(mod(u_time * 0.1, 6.0))];

      gl_FragColor = vec4(color, opacity);
    }
  `;

  return (
    <ShaderMaterialComponent
      source={shaderSource}
      uniforms={uniforms}
      maxFps={60}
    />
  );
};


const CanvasRevealEffect = ({
  animationSpeed = 0.4,
  opacities = [0.3, 0.3, 0.3, 0.5, 0.5, 0.5, 0.8, 0.8, 0.8, 1],
  colors = [[0, 255, 255]],
  containerClassName,
  dotSize,
  showGradient = true,
}) => {
  return (
    <div className={containerClassName}>
      <Canvas className="absolute inset-0 h-full w-full">
        <DotMatrix
          colors={colors.map(
            (color) =>
              new THREE.Color(color[0] / 255, color[1] / 255, color[2] / 255)
          )}
          opacities={opacities}
          totalSize={4}
          dotSize={dotSize ?? 3}
          center={['x', 'y']}
        />
      </Canvas>
      {showGradient && <div className="gradient-overlay" />}
    </div>
  );
};

export default CanvasRevealEffect;

